import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AlertCircle, Eye, EyeOff, Lock, Mail, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { affiliateApi, saveAffiliateSession } from '../services/affiliateApi';

type Mode = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
}

interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const AffiliatesAuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verificationSentTo, setVerificationSentTo] = useState<string | null>(null);
  const googleSignInRef = useRef<HTMLDivElement>(null);

  const loginForm = useForm<LoginForm>();
  const signupForm = useForm<SignupForm>();
  const signupPassword = signupForm.watch('password');

  const handleGoogleSignIn = useCallback(async (response: any) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const credential =
        typeof response === 'string'
          ? response
          : typeof response?.credential === 'string'
            ? response.credential
            : response?.credential?.credential;
      if (!credential || typeof credential !== 'string') {
        throw new Error('No credential received from Google');
      }
      const result = await affiliateApi.googleLogin(credential);
      saveAffiliateSession(result.token, result.affiliate);
      toast.success('Signed in');
      navigate('/affiliates/dashboard');
    } catch (error: any) {
      const message =
        error.response?.data?.message || error.message || 'Google sign-in failed. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if ((window as any).google && googleSignInRef.current) {
        googleSignInRef.current.innerHTML = '';
        (window as any).google.accounts.id.initialize({
          client_id:
            process.env.REACT_APP_GOOGLE_CLIENT_ID ||
            '946690405620-31eg6dhvpvf2mud0t67dtdmo6bjbkpif.apps.googleusercontent.com',
          callback: handleGoogleSignIn,
        });
        (window as any).google.accounts.id.renderButton(googleSignInRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          width: '100%',
        });
      }
    };

    if ((window as any).google) {
      initializeGoogleSignIn();
      return;
    }
    const checkInterval = setInterval(() => {
      if ((window as any).google) {
        initializeGoogleSignIn();
        clearInterval(checkInterval);
      }
    }, 100);
    const timeout = setTimeout(() => clearInterval(checkInterval), 10000);
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [handleGoogleSignIn, mode]);

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const result = await affiliateApi.login(data.email.trim().toLowerCase(), data.password);
      saveAffiliateSession(result.token, result.affiliate);
      toast.success('Signed in');
      navigate('/affiliates/dashboard');
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupForm) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await affiliateApi.register({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });
      setVerificationSentTo(data.email.trim().toLowerCase());
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Could not create your affiliate account.');
    } finally {
      setIsLoading(false);
    }
  };

  const resend = async () => {
    if (!verificationSentTo) return;
    setIsLoading(true);
    try {
      await affiliateApi.resendVerification(verificationSentTo);
      toast.success('Verification email sent');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not resend the verification email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col no-animations">
      <CustomerNavbar showDashboardLink={false} showLogoutButton={false} showAuthButtons={true} />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                {mode === 'signup' && !verificationSentTo ? (
                  <UserPlus className="h-10 w-10 text-blue-900" strokeWidth={2} />
                ) : verificationSentTo ? (
                  <Mail className="h-10 w-10 text-blue-900" strokeWidth={2} />
                ) : (
                  <Lock className="h-10 w-10 text-blue-900" strokeWidth={2} />
                )}
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {verificationSentTo ? 'Verification email sent' : 'Affiliate Program'}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {verificationSentTo
                ? 'Verify your email, then sign in. Your partner account stays pending until an administrator approves it.'
                : 'Partner accounts are separate from customer and business sign-in.'}
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
            {verificationSentTo ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    We sent a verification link to <span className="font-medium">{verificationSentTo}</span>.
                    After you verify, sign in to the affiliate dashboard. Your account stays pending until it is approved.
                  </p>
                </div>
                <button type="button" onClick={resend} disabled={isLoading} className="aacore-btn-outlined w-full justify-center">
                  {isLoading ? 'Sending...' : 'Resend verification email'}
                </button>
                <button
                  type="button"
                  className="w-full text-sm font-medium text-blue-900 hover:text-blue-800 bg-transparent border-0"
                  onClick={() => {
                    setVerificationSentTo(null);
                    setMode('login');
                    setErrorMessage(null);
                  }}
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setErrorMessage(null);
                    }}
                    className={`rounded-full px-4 py-2 text-sm font-medium border ${
                      mode === 'login'
                        ? 'bg-[#0B4778] text-white border-[#0B4778]'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signup');
                      setErrorMessage(null);
                    }}
                    className={`rounded-full px-4 py-2 text-sm font-medium border ${
                      mode === 'signup'
                        ? 'bg-[#0B4778] text-white border-[#0B4778]'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    Sign up
                  </button>
                </div>

                {mode === 'login' ? (
                  <form className="space-y-6" onSubmit={loginForm.handleSubmit(onLogin)} noValidate>
                    <div>
                      <label htmlFor="affiliate-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email address
                      </label>
                      <input
                        id="affiliate-email"
                        type="email"
                        autoComplete="email"
                        className="aacore-textfield-primary"
                        placeholder="Enter your email"
                        {...loginForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                        })}
                      />
                      {loginForm.formState.errors.email && (
                        <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="affiliate-password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="affiliate-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          className="aacore-textfield-primary pr-10"
                          placeholder="Enter your password"
                          {...loginForm.register('password', {
                            required: 'Password is required',
                            minLength: { value: 6, message: 'Password must be at least 6 characters' },
                          })}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword((value) => !value)}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                        </button>
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.password.message}</p>
                      )}
                    </div>
                    {errorMessage && <ErrorBanner message={errorMessage} />}
                    <button type="submit" disabled={isLoading} className="aacore-btn-primary w-full justify-center">
                      {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                  </form>
                ) : (
                  <form className="space-y-5" onSubmit={signupForm.handleSubmit(onSignup)} noValidate>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="affiliate-first-name" className="block text-sm font-medium text-gray-700 mb-2">
                          First name
                        </label>
                        <input
                          id="affiliate-first-name"
                          className="aacore-textfield-primary"
                          {...signupForm.register('firstName', { required: 'First name is required' })}
                        />
                        {signupForm.formState.errors.firstName && (
                          <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.firstName.message}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="affiliate-last-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Last name
                        </label>
                        <input
                          id="affiliate-last-name"
                          className="aacore-textfield-primary"
                          {...signupForm.register('lastName', { required: 'Last name is required' })}
                        />
                        {signupForm.formState.errors.lastName && (
                          <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.lastName.message}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="affiliate-signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email address
                      </label>
                      <input
                        id="affiliate-signup-email"
                        type="email"
                        autoComplete="email"
                        className="aacore-textfield-primary"
                        {...signupForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                        })}
                      />
                      {signupForm.formState.errors.email && (
                        <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="affiliate-signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        id="affiliate-signup-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="aacore-textfield-primary"
                        {...signupForm.register('password', {
                          required: 'Password is required',
                          minLength: { value: 6, message: 'Password must be at least 6 characters' },
                        })}
                      />
                      {signupForm.formState.errors.password && (
                        <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.password.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="affiliate-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm password
                      </label>
                      <input
                        id="affiliate-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        className="aacore-textfield-primary"
                        {...signupForm.register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (value) => value === signupPassword || 'Passwords do not match',
                        })}
                      />
                      {signupForm.formState.errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>
                    {errorMessage && <ErrorBanner message={errorMessage} />}
                    <button type="submit" disabled={isLoading} className="aacore-btn-primary w-full justify-center">
                      {isLoading ? 'Creating account...' : 'Create affiliate account'}
                    </button>
                  </form>
                )}

                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center" ref={googleSignInRef} />
                  <p className="mt-3 text-xs text-gray-500 text-center">
                    Google sign-in uses a separate affiliate account. Customer and business logins are not affected.
                  </p>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-sm text-gray-600">
            Looking for a business or customer account?{' '}
            <Link to="/login" className="font-medium text-blue-900 hover:text-blue-800">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

export default AffiliatesAuthPage;
